import React, { useState, useEffect, useCallback } from 'react';
import { getPrescriptionSuggestions } from '../../../lib/prescriptionSuggestions';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { GymSessionEditor } from '../../../components/gym/GymSessionEditor';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  ChevronLeft, Dumbbell, Wind, ShieldCheck, Heart, ClipboardList, Users, User,
  CheckCircle, Clock, XCircle, AlertTriangle, Trash2, Calendar, Sparkles, MinusCircle,
  Pencil, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  PLYOMETRIC: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  MIXED: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart, PLYOMETRIC: Zap, MIXED: ClipboardList };
const JUMP_FOCUS_LABELS = {
  REACTIVE_STRENGTH: 'Reactive Strength', ELASTIC_STIFFNESS: 'Elastic / Stiffness',
  EXPLOSIVE_POWER: 'Explosive Power', ASYMMETRY_CORRECTION: 'Asymmetry Correction',
  NEURAL_ACTIVATION: 'Neural Activation', LANDING_DECELERATION_CONTROL: 'Landing / Deceleration',
  RTP_RETURN_TO_JUMP: 'RTP Return-to-Jump',
};
const STATUS_STYLES = {
  ASSIGNED: { icon: Clock, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', label: 'Assigned' },
  IN_PROGRESS: { icon: Clock, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', label: 'In Progress' },
  COMPLETED: { icon: CheckCircle, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', label: 'Completed' },
  PARTIAL: { icon: MinusCircle, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', label: 'Partial' },
  SKIPPED: { icon: XCircle, color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20', label: 'Skipped' },
  MISSED: { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/20', label: 'Missed' },
};
const ALL_STATUSES = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'PARTIAL', 'SKIPPED', 'MISSED'];
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function PrescriptionDetailPage() {
  const { language } = useLanguage();
  const { api, hasRole } = useAuth();
  const navigate = useNavigate();
  const { prescriptionId } = useParams();
  const [rx, setRx] = useState(null);
  const [loading, setLoading] = useState(true);
  const canDelete = hasRole(['ADMIN', 'SNC']);
  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/prescriptions/${prescriptionId}`);
      setRx(res.data);
    } catch (e) {
      toast.error('Prescription not found');
      navigate('/staff/prescriptions');
    } finally {
      setLoading(false);
    }
  }, [api, prescriptionId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this prescription and all assignments?')) return;
    try {
      await api.delete(`/prescriptions/${prescriptionId}`);
      toast.success('Prescription deleted');
      navigate('/staff/prescriptions');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (assignmentId, newStatus) => {
    try {
      await api.put(`/prescriptions/assignments/${assignmentId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  // p05.1 — Complete All action. When the prescription has a linked canonical
  // session (rx.session_id), we reuse the iter_123-hardened session bulk endpoint.
  // Otherwise (legacy prescription-only) we fall back to per-assignment PUTs.
  const [showBulkCompleteDialog, setShowBulkCompleteDialog] = useState(false);
  const [isBulkCompleting, setIsBulkCompleting] = useState(false);

  const handleBulkComplete = async () => {
    if (isBulkCompleting) return;
    setIsBulkCompleting(true);
    try {
      const items = (rx?.assignments || []);
      const incomplete = items.filter((a) => a.status !== 'COMPLETED');
      // Update via per-assignment PUT — SSOT for rx.assignments is
      // prescription_assignments. The session bulk-status endpoint updates
      // session_assignments (a parallel collection).
      await Promise.all(
        incomplete.map((a) =>
          api.put(`/prescriptions/assignments/${a.id}`, { status: 'COMPLETED' })
        )
      );
      toast.success(`Marked ${incomplete.length} athletes as completed`);
      setShowBulkCompleteDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete all');
    } finally {
      setIsBulkCompleting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="text-zinc-500">Loading...</div></div>;
  if (!rx) return <div className="text-center py-12 text-zinc-500">Not found</div>;

  const Icon = CAT_ICONS[rx.category] || ClipboardList;
  const assignments = rx.assignments || [];
  const completedCount = assignments.filter(a => a.status === 'COMPLETED').length;
  const isMicrocycle = rx.prescription_type === 'MICROCYCLE';
  const athleteData = rx.athlete_data || {};

  // Helper: find athlete suggestions for a given block/exercise
  const getAthleteSuggestions = (block, exercise) => {
    return getPrescriptionSuggestions(athleteData, block, exercise);
  };

  const renderBlock = (block, idx, section = '') => {
    return (
      <div key={idx} data-testid={`prescription-block-${section}${idx}`} className={`border rounded-sm p-3 ${CAT_COLORS[block.block_type] || 'border-zinc-700 bg-zinc-900/30'}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-zinc-200">{block.name || `Block ${idx + 1}`}</span>
          <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{block.block_type}</Badge>
          {block.training_objective && <Badge className="text-[10px] bg-zinc-700/50 text-zinc-300">{block.training_objective.replace(/_/g, ' ')}</Badge>}
          {block.jump_focus && <Badge className="text-[10px] bg-orange-500/15 text-orange-400 border-orange-500/30">{JUMP_FOCUS_LABELS[block.jump_focus] || block.jump_focus}</Badge>}
        </div>
        <div className="space-y-1.5">
          {(block.exercises || []).map((ex, ei) => {
            const suggestions = getAthleteSuggestions(block, ex);
            return (
              <div key={ei}>
                <div className="flex items-center justify-between text-xs bg-zinc-900/40 rounded px-2 py-1.5">
                  <span className="text-zinc-200 font-medium">{ex.exercise_name}</span>
                  <div className="flex items-center gap-3 text-zinc-400">
                    {ex.sets && <span>{ex.sets}x{ex.reps || '-'}</span>}
                    {ex.load_kg && <span>{ex.load_kg}kg</span>}
                    {ex.velocity_target && <span>{ex.velocity_target}m/s</span>}
                    {ex.work_sec && <span>{ex.work_sec}s work</span>}
                    {ex.rest_sec && <span>{ex.rest_sec}s rest</span>}
                    {ex.intensity_pct && <span>{ex.intensity_pct}%</span>}
                    {ex.duration_sec && <span>{Math.round(ex.duration_sec / 60)}min</span>}
                    {ex.distance_m && <span>{ex.distance_m}m</span>}
                    {ex.target_speed_kmh && <span>{ex.target_speed_kmh}km/h</span>}
                    {ex.contacts && <span>{ex.contacts} contacts</span>}
                    {ex.intensity_level && <span>{ex.intensity_level}</span>}
                  </div>
                </div>
                {/* Athlete-specific data */}
                {ex.load_mode === 'PCT_BW' && <div className="mt-2 text-xs space-y-1" data-testid={`saved-bw-${section}${idx}-${ei}`}>
                  {Object.entries(ex.body_weight_suggestions || {}).map(([aid, row]) => <div key={aid} className="flex flex-wrap gap-2" data-testid={`saved-bw-${section}${idx}-${ei}-${aid}`}>
                    <span>{row.athlete_name}</span><span className="text-emerald-400">{row.load_kg == null ? (language === 'IT' ? 'Peso non disponibile' : 'Weight unavailable') : `${row.load_kg} kg`}</span>
                    {row.has_weight && <span className="text-zinc-400">BW {row.weight_kg} kg · {row.date} · {ex.load_pct_bw}%</span>}
                  </div>)}
                </div>}
                {suggestions.length > 0 && (
                  <div className="ml-2 mt-1 mb-2 border-l-2 border-amber-500/30 pl-2 space-y-0.5" data-testid={`athlete-data-${section}${idx}-${ei}`}>
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Per-Athlete Values</span>
                    </div>
                    {suggestions.map((s, si) => (
                      <div key={si} className="text-[11px] bg-zinc-800/50 rounded px-2 py-1 flex items-center justify-between">
                        <span className="text-zinc-300 font-medium min-w-[100px]">{s.athlete_name || s.athlete_id}</span>
                        <div className="flex items-center gap-3 flex-wrap justify-end">
                          {s.warning ? (
                            <span className="text-amber-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {s.warning}
                            </span>
                          ) : (
                            <>
                              {s.source && <Badge className="bg-zinc-700/50 text-zinc-400 text-[9px]">{s.source}</Badge>}
                              {s.mas_kmh != null && <span className="text-zinc-500">MAS: {s.mas_kmh} km/h</span>}
                              {s.target_distance_m != null && <span className="text-emerald-400 font-medium">{s.target_distance_m}m/rep</span>}
                              {s.total_distance_m != null && <span className="text-blue-400 font-medium">{s.total_distance_m}m total</span>}
                              {s.target_speed_kmh != null && <span className="text-emerald-400">{s.target_speed_kmh} km/h</span>}
                              {s.suggested_load_min_kg != null && (
                                <span className="text-emerald-400 font-medium">{s.suggested_load_min_kg}-{s.suggested_load_max_kg} kg</span>
                              )}
                              {s.suggested_velocity_min != null && (
                                <span className="text-blue-400">{s.suggested_velocity_min}-{s.suggested_velocity_max} m/s</span>
                              )}
                              {s.estimated_1rm != null && <span className="text-zinc-500">e1RM: {s.estimated_1rm}kg</span>}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Jump / Plyometric per-athlete data */}
        {block.block_type === 'PLYOMETRIC' && block.jump_focus && athleteData[`jump_${block.jump_focus}`] && (
          <div className="mt-2 border-t border-orange-500/20 pt-2" data-testid={`jump-data-block-${section}${idx}`}>
            <div className="flex items-center gap-1 mb-1.5">
              <Sparkles className="w-3 h-3 text-orange-400" />
              <span className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider">
                Jump Data — {JUMP_FOCUS_LABELS[block.jump_focus] || block.jump_focus}
              </span>
            </div>
            <div className="space-y-1">
              {Object.entries(athleteData[`jump_${block.jump_focus}`]).map(([aid, d]) => {
                const isRTP = (d.flags || []).includes('IN_RTP');
                return (
                  <div key={aid} className={`text-[11px] rounded px-2 py-1.5 ${isRTP ? 'bg-rose-500/5 border border-rose-500/30' : 'bg-zinc-800/50'}`} data-testid={`jump-detail-${aid}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-300 font-medium">{d.athlete_name || aid}</span>
                      <div className="flex gap-1">
                        {(d.flags || []).map(f => (
                          <Badge key={f} className={`text-[8px] ${
                            f === 'IN_RTP' ? 'bg-rose-500/20 text-rose-400' :
                            f === 'LOW_RSI' ? 'bg-amber-500/20 text-amber-400' :
                            f === 'HIGH_ASYMMETRY' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-zinc-700/50 text-zinc-400'
                          }`}>{f.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px]">
                      {d.metrics?.cmj_height_cm != null && <span className="text-zinc-500">CMJ: <span className="text-zinc-300">{d.metrics.cmj_height_cm}cm</span></span>}
                      {d.metrics?.sj_height_cm != null && <span className="text-zinc-500">SJ: <span className="text-zinc-300">{d.metrics.sj_height_cm}cm</span></span>}
                      {d.metrics?.dj_rsi != null && <span className="text-zinc-500">RSI: <span className="text-zinc-300">{Number(d.metrics.dj_rsi).toFixed(2)}</span></span>}
                      {d.metrics?.asymmetry_pct != null && <span className="text-zinc-500">Asym: <span className="text-zinc-300">{d.metrics.asymmetry_pct}%</span></span>}
                    </div>
                    {d.prescribed_values && (
                      <div className="flex items-center gap-3 mt-1 text-[10px]">
                        <span className="text-orange-400 font-medium">Rx:</span>
                        {d.prescribed_values.sets && <span className="text-zinc-300">{d.prescribed_values.sets} sets</span>}
                        {d.prescribed_values.reps && <span className="text-zinc-300">{d.prescribed_values.reps} reps</span>}
                        {d.prescribed_values.contacts && <span className="text-zinc-300">{d.prescribed_values.contacts} contacts</span>}
                        {d.prescribed_values.intensity_level && <span className="text-zinc-300">{d.prescribed_values.intensity_level}</span>}
                      </div>
                    )}
                    {d.warning && (
                      <div className="flex items-center gap-1 mt-1 text-amber-400 text-[10px]">
                        <AlertTriangle className="w-3 h-3" /> {d.warning}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {block.notes && <p className="text-[11px] text-zinc-600 mt-2 italic">{block.notes}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="prescription-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/staff/prescriptions')} className="text-zinc-400 hover:text-zinc-100" data-testid="back-to-prescriptions">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`p-3 rounded-sm shrink-0 ${CAT_COLORS[rx.category]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-100 break-words" data-testid="rx-detail-title">{rx.title}</h1>
            <div className="flex items-center flex-wrap gap-2 mt-1">
              <Badge variant="outline" className={`${CAT_COLORS[rx.category]} text-xs`}>{rx.category}</Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs">
                {rx.prescription_type === 'SINGLE_SESSION' ? 'Single Session' : 'Microcycle'}
              </Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs">
                {rx.target_type}
              </Badge>
              {rx.session_id && <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 text-xs">Planner Linked</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:justify-end">
          {rx.session_id && (
            <Button variant="outline" size="sm" asChild
              className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 h-11 sm:h-9 min-h-[44px] sm:min-h-0"
              data-testid="view-in-planner-btn">
              <Link to={`/staff/planner/session/${rx.session_id}`}>
                <Calendar className="w-3.5 h-3.5 mr-1" /> View in Planner
              </Link>
            </Button>
          )}
          {canEdit && isMicrocycle && (
            <Button variant="outline" size="sm"
              onClick={() => navigate(`/staff/prescriptions/builder`, { state: { editMicrocycle: rx } })}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-11 sm:h-9 min-h-[44px] sm:min-h-0" data-testid="edit-microcycle-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" size="sm" onClick={handleDelete}
              className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 h-11 sm:h-9 min-h-[44px] sm:min-h-0"
              data-testid="delete-prescription-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {rx.session_id && !isMicrocycle && <GymSessionEditor sessionId={rx.session_id} canEdit={canEdit} prefix="prescription-linked-gym" />}
      {/* Meta info */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {rx.date && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-zinc-500" />
              <div>
                <div className="text-xs text-zinc-500">Date</div>
                <div className="text-sm text-zinc-200">{rx.date}</div>
              </div>
            </CardContent>
          </Card>
        )}
        {rx.team_name && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-zinc-500" />
              <div>
                <div className="text-xs text-zinc-500">Team</div>
                <div className="text-sm text-zinc-200">{rx.team_code} - {rx.team_name}</div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3 flex items-center gap-2">
            <User className="w-4 h-4 text-zinc-500" />
            <div>
              <div className="text-xs text-zinc-500">Assignments</div>
              <div className="text-sm text-zinc-200">{completedCount}/{assignments.length} completed</div>
            </div>
          </CardContent>
        </Card>
        {rx.created_by_name && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-3 flex items-center gap-2">
              <User className="w-4 h-4 text-zinc-500" />
              <div>
                <div className="text-xs text-zinc-500">Created by</div>
                <div className="text-sm text-zinc-200">{rx.created_by_name}</div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {rx.notes && (
        <Card className="bg-zinc-900/30 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-xs text-zinc-500 mb-1">Notes</div>
            <div className="text-sm text-zinc-300">{rx.notes}</div>
          </CardContent>
        </Card>
      )}

      {/* Blocks / Microcycle */}
      <div>
        <h2 className="text-sm font-semibold text-zinc-300 mb-3 uppercase tracking-wider">
          {isMicrocycle ? 'Weekly Schedule' : 'Session Blocks'}
        </h2>
        {isMicrocycle ? (
          <div className="space-y-4">
            {(rx.microcycle_days || []).map(day => (
              <div key={day.day_of_week}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-amber-400">{day.day_label || DAY_LABELS[day.day_of_week]}</h3>
                  {day.duration_planned_min && (
                    <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">
                      {day.duration_planned_min}m
                    </Badge>
                  )}
                  {day.session_id && (
                    <Link
                      to={`/staff/planner/session/${day.session_id}`}
                      className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25"
                      data-testid={`open-planner-day-${day.day_of_week}`}
                    >
                      <Calendar className="w-3 h-3" /> Planner
                    </Link>
                  )}
                </div>
                <div className="space-y-2 ml-4">
                  {day.session_id && <GymSessionEditor sessionId={day.session_id} canEdit={canEdit} prefix={`prescription-day-${day.day_of_week}-gym`} />}
                  {(day.blocks || []).map((b, i) => renderBlock(b, i, `day-${day.day_of_week}-`))}
                  {(day.blocks || []).length === 0 && <div className="text-xs text-zinc-600 italic">Rest day</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {(rx.blocks || []).map((b, i) => renderBlock(b, i))}
          </div>
        )}
      </div>

      {/* Assignments with status management */}
      <div>
        <div className="mb-3 flex items-start sm:items-center justify-between gap-2 flex-wrap">
          <div className="flex flex-col">
            <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">
              Assignments ({assignments.length})
            </h2>
            {assignments.length > 0 && (
              <span className="text-xs text-zinc-400 mt-0.5" data-testid="rx-assignments-completion-counter">
                {completedCount}/{assignments.length} completed
              </span>
            )}
          </div>
          {canEdit && assignments.length > 0 && completedCount < assignments.length && (
            <Button
              onClick={() => setShowBulkCompleteDialog(true)}
              size="sm"
              className="h-11 sm:h-9 min-h-[44px] sm:min-h-0 bg-emerald-600 text-white hover:bg-emerald-500 border border-emerald-500/50"
              data-testid="rx-bulk-mark-completed-button"
            >
              <CheckCircle className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs font-semibold">Mark all as completed</span>
              <span className="sm:hidden text-xs font-semibold ml-1.5">Complete all</span>
            </Button>
          )}
        </div>
        {assignments.length === 0 ? (
          <div className="text-sm text-zinc-500">No assignments</div>
        ) : isMicrocycle ? (
          /* Per-day grouped assignments for microcycles */
          <div className="space-y-5" data-testid="microcycle-assignments">
            {(() => {
              const dayMap = {};
              for (const a of assignments) {
                const dow = a.day_of_week ?? -1;
                if (!dayMap[dow]) dayMap[dow] = { label: a.day_label || DAY_LABELS[dow] || 'General', items: [] };
                dayMap[dow].items.push(a);
              }
              return Object.entries(dayMap).sort(([a],[b]) => Number(a) - Number(b)).map(([dow, group]) => {
                const dayComplete = group.items.filter(i => i.status === 'COMPLETED').length;
                return (
                  <div key={dow} data-testid={`day-group-${dow}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-amber-400">{group.label}</h3>
                      <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">
                        {dayComplete}/{group.items.length} completed
                      </Badge>
                    </div>
                    <div className="space-y-1.5 ml-4">
                      {group.items.map(a => {
                        const statusStyle = STATUS_STYLES[a.status] || STATUS_STYLES.ASSIGNED;
                        const StatusIcon = statusStyle.icon;
                        return (
                          <Card key={a.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`assignment-${a.id}`}>
                            <CardContent className="p-2.5 flex items-center justify-between">
                              <div className="flex items-center gap-2.5">
                                <div className={`p-1 rounded-sm border ${statusStyle.color}`}>
                                  <StatusIcon className="w-3.5 h-3.5" />
                                </div>
                                <div>
                                  <span className="text-xs font-medium text-zinc-200">{a.athlete_name || a.athlete_id}</span>
                                  {a.athlete_position && <span className="text-[10px] text-zinc-500 ml-1.5">{a.athlete_position}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {canEdit ? (
                                  <Select value={a.status} onValueChange={(val) => handleStatusChange(a.id, val)}>
                                    <SelectTrigger className={`w-[120px] h-6 text-[10px] border ${statusStyle.color}`} data-testid={`status-select-${a.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                      {ALL_STATUSES.map(s => (
                                        <SelectItem key={s} value={s} className="text-zinc-100 text-xs">
                                          {STATUS_STYLES[s]?.label || s}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant="outline" className={`text-[10px] ${statusStyle.color}`}>{statusStyle.label}</Badge>
                                )}
                                {a.feedback_note && (
                                  <span className="text-[10px] text-zinc-400 max-w-[140px] truncate" title={a.feedback_note}>
                                    "{a.feedback_note}"
                                  </span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          <div className="space-y-2" data-testid="assignments-list">
            {assignments.map(a => {
              const statusStyle = STATUS_STYLES[a.status] || STATUS_STYLES.ASSIGNED;
              const StatusIcon = statusStyle.icon;
              return (
                <Card key={a.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`assignment-${a.id}`}>
                  <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-1.5 rounded-sm border shrink-0 ${statusStyle.color}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium text-zinc-200 truncate block">{a.athlete_name || a.athlete_id}</span>
                        {a.athlete_position && <span className="text-xs text-zinc-500">{a.athlete_position}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      {canEdit ? (
                        <Select
                          value={a.status}
                          onValueChange={(val) => handleStatusChange(a.id, val)}
                        >
                          <SelectTrigger className={`w-[130px] h-11 sm:h-7 min-h-[44px] sm:min-h-0 text-xs border ${statusStyle.color}`} data-testid={`status-select-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {ALL_STATUSES.map(s => (
                              <SelectItem key={s} value={s} className="text-zinc-100 text-xs">
                                {STATUS_STYLES[s]?.label || s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={`text-xs ${statusStyle.color}`}>{statusStyle.label}</Badge>
                      )}
                      {a.completed_at && <span className="text-[10px] text-zinc-500">{new Date(a.completed_at).toLocaleDateString()}</span>}
                      {a.feedback_note && (
                        <span className="text-[11px] text-zinc-400 max-w-[160px] truncate" title={a.feedback_note}>
                          "{a.feedback_note}"
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk complete confirmation dialog (p05.1) */}
      {showBulkCompleteDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="rx-bulk-mark-completed-dialog"
          onClick={() => !isBulkCompleting && setShowBulkCompleteDialog(false)}
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-sm max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-zinc-100 mb-2">
              Mark all as completed?
            </h3>
            <p className="text-sm text-zinc-400 mb-4" data-testid="rx-bulk-mark-completed-message">
              All {assignments.length - completedCount} pending athletes on this prescription will be marked Completed. Existing completed assignments will remain unchanged.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="outline"
                className="border-zinc-700"
                onClick={() => setShowBulkCompleteDialog(false)}
                disabled={isBulkCompleting}
                data-testid="rx-bulk-mark-completed-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkComplete}
                disabled={isBulkCompleting}
                className="bg-emerald-600 text-white hover:bg-emerald-500"
                data-testid="rx-bulk-mark-completed-confirm"
              >
                {isBulkCompleting ? 'Working…' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
